// Kit 01 · items 26, 27, 28, 31, 32.
//
//   26 Form field wrapper   label to field 6 · field to help 6 · stack gap 16
//   27 Inline validation    icon 14 · gap 8 · text 12 · below the field
//   28 Fieldset             legend 13/600 · rule 1 · section gap 32 · field 16
//   31 Character counter    12 mono · right aligned · warning at 90 percent
//   32 Read-only display    no border · no fill · label above · value 14/500

import { useId } from "react";
import type { ReactNode } from "react";
import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react";
import { Icon } from "./icon";
import { cx } from "./kit-state";

/* ── 26 · field wrapper ─────────────────────────────────────────────────────
   It owns the id wiring. A label that is not `for` its control is a label that
   does not enlarge the hit area and does not get read, and the only way to be
   sure is for one component to mint both ids. */

export interface FieldRowProps {
  label: string;
  children: (ids: { id: string; describedBy?: string }) => ReactNode;
  help?: string;
  error?: string;
  required?: boolean;
  className?: string;
}

export function FieldRow({ label, children, help, error, required, className }: FieldRowProps) {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const describedBy = [help ? helpId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cx("k-fieldrow", className)}>
      <label className="k-fieldrow__label" htmlFor={id}>
        {label}
        {required ? <span className="k-fieldrow__req" aria-hidden="true"> *</span> : null}
        {required ? <span className="k-sr"> required</span> : null}
      </label>
      {children({ id, describedBy })}
      {help ? <span className="k-fieldrow__help" id={helpId}>{help}</span> : null}
      {error ? <Validation id={errorId} tone="error">{error}</Validation> : null}
    </div>
  );
}

export function FormStack({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("k-form-stack", className)}>{children}</div>;
}

/* ── 27 · inline validation ─────────────────────────────────────────────── */

export type ValidationTone = "error" | "warn" | "ok" | "info";

const TONE_ICON = {
  error: CircleAlert,
  warn: TriangleAlert,
  ok: CircleCheck,
  info: Info,
} as const;

export function Validation({
  tone = "error", children, id, className,
}: { tone?: ValidationTone; children: ReactNode; id?: string; className?: string }) {
  return (
    <span
      id={id}
      className={cx("k-validation", `k-validation--${tone}`, className)}
      // An error must be announced when it appears, not only when the field is
      // next read. A warning or a hint must not interrupt.
      role={tone === "error" ? "alert" : undefined}
    >
      <Icon icon={TONE_ICON[tone]} size="sm" />
      {children}
    </span>
  );
}

/* ── 28 · fieldset ──────────────────────────────────────────────────────── */

export function Fieldset({
  legend, children, className,
}: { legend: string; children: ReactNode; className?: string }) {
  return (
    <fieldset className={cx("k-fieldset", className)}>
      <legend className="k-fieldset__legend">{legend}</legend>
      {children}
    </fieldset>
  );
}

export function FieldsetStack({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("k-fieldset-stack", className)}>{children}</div>;
}

/* ── 31 · character counter ─────────────────────────────────────────────────
   redline: 12 mono · right aligned under the field · warning at 90 percent
   The 90% threshold is DERIVED from max, never passed in beside it. Passing
   both is how a counter comes to warn at 180 of 250. */

export const COUNTER_WARN_AT = 0.9;

export function CharacterCounter({
  value, max, className,
}: { value: string; max: number; className?: string }) {
  const used = value.length;
  const warning = used >= Math.floor(max * COUNTER_WARN_AT);
  const over = used > max;
  return (
    <span
      className={cx("k-counter", className)}
      data-warning={warning && !over ? "true" : undefined}
      data-over={over ? "true" : undefined}
      data-num="true"
      // Counting on every keystroke is noise. It matters at the boundary.
      aria-live={warning ? "polite" : "off"}
    >
      {used} / {max}
    </span>
  );
}

/* ── 32 · read-only display ─────────────────────────────────────────────────
   Not a control: no border, no fill, nothing that invites a caret.
   `value` may be absent, and absence renders as the canonical string — never
   0, never a dash, never an empty cell. design-lint Rule 11. */

// Phase 1 declared this here because item 32 was the first component that
// needed it. Phase 3 needed it in four more places, so the words moved to
// strings.ts and this is now a re-export: one set of characters, one file, and
// no way for two components to hold two spellings of the same rule.
export { NOT_MEASURED } from "./strings";
import { NOT_MEASURED } from "./strings";

export function Readout({
  label, value, className,
}: { label: string; value: ReactNode | null | undefined; className?: string }) {
  const missing = value === null || value === undefined || value === "";
  return (
    <div className={cx("k-readout", className)}>
      <span className="k-readout__label">{label}</span>
      <span className="k-readout__value" data-unmeasured={missing ? "true" : undefined}>
        {missing ? NOT_MEASURED : value}
      </span>
    </div>
  );
}
